export interface ApiError extends Error {
  type: ErrorType
  message: string
  /**
   * The HTTP status, when the failure carried one. Absent for network errors and
   * anything that never reached the backend, which is itself meaningful — see
   * {@link isPermanentRejection}.
   */
  status?: number
}

export type ErrorType
  = | 'INVALID_ACKNOWLEDGEMENT'
    | 'INVALID_URL_CODE'
    | 'INVALID_VERIFICATION_CODE'
    | 'NETWORK_CONNECTION'
    | 'NO_REGION'
    | 'NO_TOKEN'
    | 'NOT_YET_LINKED'
    | 'PERSISTENCE_OUT_OF_SYNC'
    | 'REGISTRATION_FAILED'
    | 'SSO_LOGIN_FAILED'
    | 'SSO_DESTINATION_NOT_FOUND'
    | 'STORE_NOT_FOUND'
    | 'FORBIDDEN'
    | 'UNKNOWN'

export const API_ERROR_TYPE: Record<string, ErrorType> = {
  INVALID_ACKNOWLEDGEMENT: 'INVALID_ACKNOWLEDGEMENT',
  INVALID_URL_CODE: 'INVALID_URL_CODE',
  INVALID_VERIFICATION_CODE: 'INVALID_VERIFICATION_CODE',
  NETWORK_CONNECTION: 'NETWORK_CONNECTION',
  NO_REGION: 'NO_REGION',
  NO_TOKEN: 'NO_TOKEN',
  NOT_YET_LINKED: 'NOT_YET_LINKED',
  PERSISTENCE_OUT_OF_SYNC: 'PERSISTENCE_OUT_OF_SYNC',
  REGISTRATION_FAILED: 'REGISTRATION_FAILED',
  SSO_LOGIN_FAILED: 'SSO_LOGIN_FAILED',
  SSO_DESTINATION_NOT_FOUND: 'SSO_DESTINATION_NOT_FOUND',
  STORE_NOT_FOUND: 'STORE_NOT_FOUND',
  FORBIDDEN: 'FORBIDDEN',
  UNKNOWN: 'UNKNOWN',
}

/**
 * Reads the HTTP status off whatever the failure happens to be: an ofetch `FetchError`
 * (`status` / `statusCode`), a raw `Response`, or an `ApiError` this function produced
 * earlier — `apiFetch` maps twice, at `onResponseError` and again in its own catch.
 */
function readErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const candidate = error as { status?: unknown, statusCode?: unknown }
  const status = typeof candidate.status === 'number' ? candidate.status : undefined
  return status ?? (typeof candidate.statusCode === 'number' ? candidate.statusCode : undefined)
}

/**
 * @description Creates a typed error handler
 */
export function handleApiError(error: unknown): ApiError {
  const apiError = new Error('An unexpected error occurred') as ApiError

  // Carried through before any early return: this used to build a fresh Error and copy
  // only the message, so the status was silently lost and every caller that branched on
  // it saw `undefined`.
  const status = readErrorStatus(error)
  if (status !== undefined) {
    apiError.status = status
  }

  // Handle Response objects (from fetch API)
  if (error instanceof Response || (error && typeof error === 'object' && 'status' in error)) {
    const response = error as Response & { status?: number }

    if (response.status === 403) {
      apiError.type = API_ERROR_TYPE.FORBIDDEN
      apiError.message = 'Access denied. Your session may have expired.'
      return apiError
    }
  }

  if (error instanceof Error) {
    // Network error check
    if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
      apiError.type = API_ERROR_TYPE.NETWORK_CONNECTION
      apiError.message = 'Network connection failed. Please check your connection and try again.'
      return apiError
    }

    // Copy the original error message
    apiError.message = error.message
  }

  // Default error
  apiError.type = API_ERROR_TYPE.UNKNOWN
  apiError.message = apiError.message || 'An unexpected error occurred. Please try again later.'
  return apiError
}

/**
 * @description Creates a properly typed API error
 */
export function createApiError(type: ErrorType, message: string): ApiError {
  const error = new Error(message) as ApiError
  error.type = type
  return error
}

/**
 * Whether the backend has refused a request for good.
 *
 * A 4xx is the backend saying the payload is wrong, so retrying changes nothing — it
 * would fail identically every time. Network failures, timeouts and 5xx are the
 * opposite: the request is fine and the attempt was not. 408 and 429 are 4xx but
 * explicitly mean "try again".
 */
export function isPermanentRejection(error: unknown): boolean {
  const candidate = error as { status?: number, statusCode?: number } | null
  const status = candidate?.status ?? candidate?.statusCode

  if (typeof status !== 'number') {
    return false
  }
  if (status === 408 || status === 429) {
    return false
  }
  return status >= 400 && status < 500
}
